import { createSlice } from "@reduxjs/toolkit";
import { logOut } from "../auth/authSlice";
import { getOrders, getReorderPreview, placeReorder } from "./ordersActions";

// Quick Reorder modal state. `preview` is the GET answer for the order being
// reordered; `error` is the rejected payload of the last preview or place
// call. The page owns which order the modal is for.
const initialReorder = {
  loading: false, // GET preview in flight
  placing: false, // POST place in flight
  preview: null,
  error: null,
};

const initialState = {
  loading: false,
  error: null,
  items: [], // current page of the order history list (results)
  meta: null, // pagination meta from the server
  // Last status push from the shop socket: { seq, order, previous_status }.
  // `seq` grows by one per push so the Orders page can tell a new push from
  // a re-render. The page reads it for the toast and to decide on a refetch.
  lastUpdate: null,
  reorder: initialReorder,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    resetOrdersError: (state) => {
      state.error = null;
    },
    // Closing the reorder modal: drop the preview and any error so the next
    // order starts clean.
    resetReorder: (state) => {
      state.reorder = initialReorder;
    },
    // A status push from the shop socket. Replace the row if it is on the
    // current page; otherwise leave the list alone (it belongs to another
    // page, or to a filter the customer is not looking at). Never append:
    // pagination and the active status filter are the server's, not ours.
    socketOrderUpdate: (state, action) => {
      const order = action.payload?.order;
      if (!order?.id) return;
      const index = state.items.findIndex((row) => row?.id === order.id);
      if (index !== -1) {
        state.items[index] = order;
      }
      state.lastUpdate = {
        seq: (state.lastUpdate?.seq || 0) + 1,
        order,
        previous_status: action.payload?.previous_status ?? null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload?.results || [];
        state.meta = action.payload?.meta || null;
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getReorderPreview.pending, (state) => {
        state.reorder.loading = true;
        state.reorder.preview = null;
        state.reorder.error = null;
      })
      .addCase(getReorderPreview.fulfilled, (state, action) => {
        state.reorder.loading = false;
        // Only a JSON object is a preview; anything else (an HTML page from
        // a redirect, an empty body) is dropped.
        state.reorder.preview =
          action.payload && typeof action.payload === "object"
            ? action.payload
            : null;
      })
      .addCase(getReorderPreview.rejected, (state, action) => {
        state.reorder.loading = false;
        state.reorder.error = action.payload;
      })
      .addCase(placeReorder.pending, (state) => {
        state.reorder.placing = true;
        state.reorder.error = null;
      })
      .addCase(placeReorder.fulfilled, (state) => {
        state.reorder.placing = false;
      })
      .addCase(placeReorder.rejected, (state, action) => {
        state.reorder.placing = false;
        state.reorder.error = action.payload;
      })
      // Logout: the local copy is cleared so another customer's orders never
      // flash on this browser before the next fetch.
      .addCase(logOut, (state) => {
        state.items = [];
        state.meta = null;
        state.lastUpdate = null;
        state.reorder = initialReorder;
      });
  },
});

export const { resetOrdersError, resetReorder, socketOrderUpdate } =
  ordersSlice.actions;
export default ordersSlice.reducer;
